// Utils
import { prop } from '../../common/utils/immutableHelper';
import platformSingleton from '../platform/platformSingleton';
import { isPlatformIos } from '../platform/utils/platformDetailsUtils';
import { getScaleX, getTranslation as getMatrixTranslation } from '../../common/maths/matrix/matrixUtils';

// Constants
import { GRID } from './grid/gridConstants';

export const toCssUrl = (source) => `url(${source})`;
export const toCssPx = (value) => `${value}px`;

export const parseCssPx = (px) => parseFloat(px.replace('px', ''));

const getPoint = (translationPoint) => ({
    x: prop('x', translationPoint),
    y: prop('y', translationPoint),
});

export const getCssTransform = (transform) => ({
    transform,
    WebkitTransform: transform,
});

export const getRotationStyle = (rotation, measurement = 'rad') => getCssTransform(`rotate(${rotation}${measurement})`);

const getTranslation = ({ x, y, suffix = 'px', transformAppend = '' }) =>
    getCssTransform(`translate3d(${x}${suffix}, ${y}${suffix}, 0) ${transformAppend}`);

export const getTranslationStylesEms = (translationPoint, gridSize, fontSize = 16) => {
    const translatedFontSize = fontSize * (gridSize / GRID.LARGE.size);

    const { x, y } = getPoint(translationPoint);
    const xEm = x / translatedFontSize;
    const yEm = y / translatedFontSize;
    return getTranslation({ x: xEm, y: yEm, suffix: 'em' });
};

export const getTranslationStyles = (translationPoint, transformAppend) => {
    if (!translationPoint) return transformAppend;

    const { x, y } = getPoint(translationPoint);
    return getTranslation({ x, y, transformAppend });
};

export const convertToCssTransformation = (translationPoint, scale = 1) => {
    if (!translationPoint) return;

    return getTranslation({ ...translationPoint, transformAppend: `scale(${scale})` });
};

/**
 * Will only use a 3D transform on non-iOS devices, as the cost of creating a new layer on iOS
 * is too high for large boards.
 */
export const getIosSafeCssTranslation = (offset) => {
    if (isPlatformIos(platformSingleton)) {
        return `translate(${offset.x}px, ${offset.y}px)`;
    }

    return `translate3d(${offset.x}px, ` + `${offset.y}px, 0)`;
};

/**
 * Gets a CSS rotation string, unless on iOS - in which case we don't perform any rotation
 * as it will create a new layer and cause high memory usage on large boards.
 */
export const getIosSafeCssRotation = (rotation) => {
    if (isPlatformIos(platformSingleton)) return '';

    return `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`;
};

/**
 * Creates a CSS matrix3d transformation string based on specified transformation matrix.
 */
export const convertMatrixToCssTransformString = (transformationMatrix) => {
    const scale = getScaleX(transformationMatrix);
    const translationPoint = getMatrixTranslation(transformationMatrix);

    // On iOS devices - we want to use a 2D transform, rather than a 3D transform, for zooming, because using a 3D
    //   transform causes the canvas-document to be rendered on a different layer - thus resulting in a new layer.
    // This would cause high memory usage and often results in crashes.
    if (isPlatformIos(platformSingleton)) {
        return `translate(${translationPoint.x}px, ${translationPoint.y}px) scale(${scale})`;
    }

    // Other platforms should capitalise on the performance gains of using a new layer
    return `matrix3d(${transformationMatrix.toString()})`;
};

/**
 * Creates a CSS transform object, appropriate to be set as a style object on a React component,
 * for a transformation matrix.
 */
export const convertMatrixToCssTransform = (transformationMatrix) =>
    getCssTransform(convertMatrixToCssTransformString(transformationMatrix));

/**
 * Create a CSS transformOrigin object, in order to specify a point where the transformation should anchored on
 */
export const getTransformOrigin = (point) => {
    if (!point) return null;
    return { transformOrigin: `${point.x}px ${point.y}px` };
};
